import React from 'react';
import { DollarSign, Clock, CheckCircle2, XCircle, ArrowRightLeft, Timer, AlertTriangle } from 'lucide-react';
import { formatCurrency } from '@cosbyte/utils';

// A party arrives as a public card ({ id, username, ... }), as a bare id (older payloads) or only as
// initiatorId / recipientId on the chat order row
const partyId = (party, fallbackId) =>
    (party && typeof party === 'object' ? (party.id || party._id) : party) || fallbackId || null;

// Amounts arrive as strings from the API (Decimal columns)
const formatAmount = (amount) => {
    const value = Number(amount);
    return Number.isFinite(value) ? formatCurrency(value, 'GHS') : '';
};

const ChatOrderCard = ({ order, currentUserId, onAccept, onNegotiate, onReject }) => {
    const isRecipient = partyId(order.recipient, order.recipientId) === currentUserId;
    const isInitiator = partyId(order.initiator, order.initiatorId) === currentUserId;
    const recipientUsername = order.recipient && typeof order.recipient === 'object' ? order.recipient.username : null;
    const status = order.status;
    const isActiveProposal = status === 'pending' || status === 'negotiating';
    // Prisma sends the history as the `negotiations` relation (proposedById); older payloads used
    // negotiationHistory (proposedBy)
    const negotiations = Array.isArray(order.negotiations) ? order.negotiations : (order.negotiationHistory || []);
    const proposerId = (entry) => entry.proposedById ?? partyId(entry.proposedBy);

    const getStatusStyle = () => {
        switch (status) {
            case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'negotiating': return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'accepted': return 'bg-green-100 text-green-800 border-green-200';
            case 'paid': return 'bg-green-600 text-white border-green-700';
            case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
            case 'expired': return 'bg-gray-100 text-gray-800 border-gray-200';
            case 'cancelled': return 'bg-gray-200 text-gray-800 border-gray-300';
            default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    return (
        <div className={`w-80 md:w-96 rounded-2xl shadow-lg border-2 overflow-hidden bg-white ${isActiveProposal ? 'border-blue-100 ring-4 ring-blue-50' : 'border-gray-100'
            }`}>
            <div className={`px-4 py-2 flex justify-between items-center text-xs font-bold uppercase tracking-wider border-b ${getStatusStyle()}`}>
                <span className="flex items-center gap-1">
                    {status === 'pending' && <Timer className="w-4 h-4" />}
                    {(status === 'accepted' || status === 'paid') && <CheckCircle2 className="w-4 h-4" data-testid="chat-order-accepted-icon" />}
                    {status === 'rejected' && <XCircle className="w-4 h-4" />}
                    {status === 'expired' && <AlertTriangle className="w-4 h-4" />}
                    {status}
                </span>
                {isActiveProposal && (
                    <span className="animate-pulse">Active Proposal</span>
                )}
            </div>

            <div className="p-5 space-y-4">
                <div>
                    <h3 className="text-xl font-bold text-gray-900 line-clamp-1">{order.title}</h3>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-3">{order.description}</p>
                </div>

                <div className="flex items-center justify-between py-3 border-y border-gray-50">
                    <div className="flex flex-col">
                        <span className="text-[10px] text-gray-400 uppercase font-bold tracking-tight">Total Amount</span>
                        <span className="text-2xl font-black text-blue-600">{formatAmount(order.amount)}</span>
                    </div>
                    <div className="flex flex-col items-end">
                        <span className="text-[10px] text-gray-400 uppercase font-bold tracking-tight">Estimated Delivery</span>
                        <span className="text-sm font-bold text-gray-700 flex items-center gap-1">
                            <Clock className="w-4 h-4" /> {order.duration || 'Not set'}
                        </span>
                    </div>
                </div>

                {order.deliverables && (
                    <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
                        <span className="text-[10px] text-blue-400 uppercase font-bold tracking-tight mb-1 block">Deliverables</span>
                        <p className="text-xs text-blue-700 italic">{order.deliverables}</p>
                    </div>
                )}

                {negotiations.length > 0 && (
                    <div className="space-y-2">
                        <span className="text-[10px] text-gray-400 uppercase font-bold tracking-tight">Negotiation History</span>
                        <div className="max-h-24 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                            {negotiations.map((entry, idx) => (
                                <div key={entry.id || idx} className="bg-gray-50 rounded-lg p-2 text-xs flex justify-between items-center">
                                    <span className={proposerId(entry) === currentUserId ? 'text-blue-600 font-bold' : 'text-gray-600'}>
                                        {proposerId(entry) === currentUserId ? 'You' : 'Counter'}
                                    </span>
                                    <span className="font-bold">{formatAmount(entry.amount)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {isRecipient && isActiveProposal && (
                    <div className="flex flex-col gap-2 pt-2">
                        <button
                            onClick={() => onAccept(order)}
                            className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold transition-all shadow-md active:scale-95 flex items-center justify-center gap-2"
                        >
                            <CheckCircle2 className="w-5 h-5" /> Accept Proposal
                        </button>
                        <div className="flex gap-2">
                            <button
                                onClick={() => onNegotiate(order)}
                                className="flex-1 py-2 border-2 border-blue-600 text-blue-600 hover:bg-blue-50 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-1"
                            >
                                <ArrowRightLeft className="w-4 h-4" /> Negotiate
                            </button>
                            <button
                                onClick={() => onReject(order)}
                                className="flex-1 py-2 border-2 border-red-600 text-red-600 hover:bg-red-50 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-1"
                            >
                                <XCircle className="w-4 h-4" /> Reject
                            </button>
                        </div>
                    </div>
                )}

                {isInitiator && isActiveProposal && (
                    <div className="bg-blue-50 rounded-xl p-3 text-center">
                        <p className="text-xs text-blue-600 font-medium italic">
                            Awaiting response from {recipientUsername ? `@${recipientUsername}` : 'recipient'}...
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatOrderCard;
